import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getAntiforgeryToken, portalFetch, setAntiforgeryToken } from "../../api/http";
import { AuthActions, AuthNotice, AuthPage, SubmitButton } from "../../auth/authKit";
import { useFocusHeading } from "./focusHeading";

/** Claves de texto de la pantalla de empresa inactiva (IDN-7) y del botón de salir que ya usa el shell. */
export const inactiveOrganizationTextKeys = ["ORG_INACTIVE_", "ACCOUNT_SIGN_OUT"];

type Session = { authenticated: boolean; antiforgeryToken: string; organizationId: number | null };
type Choice = { support: boolean; options: { id: number; name: string }[]; selected: number | null };

/**
 * IDN-7 · «Tu empresa está inactiva» (/empresa/inactiva): con la empresa inactiva sus miembros no entran a ningún módulo.
 * La persona puede cerrar sesión o, si tiene otra empresa activa, ir al selector (que valida cada cambio en el servidor).
 * Qué empresas cuentan lo decide el servidor: el selector solo devuelve empresas activas con membresía activa.
 */
export function InactiveOrganizationPage({ L }: { L: (key: string) => string }) {
  const session = useQuery({ queryKey: ["session"], queryFn: () => portalFetch<Session>("/api/portal/session") });
  const choice = useQuery({
    queryKey: ["session-organizations"],
    enabled: !!session.data?.authenticated,
    queryFn: () => portalFetch<Choice>("/api/portal/session/organizations"),
    staleTime: 0,
  });
  const [sending, setSending] = useState(false);
  const title = L("ORG_INACTIVE_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);
  useEffect(() => {
    if (session.data) setAntiforgeryToken(session.data.antiforgeryToken);
    // Sin sesión no hay empresa que avisar: al ingreso.
    if (session.data && !session.data.authenticated) window.location.assign("/login");
  }, [session.data]);
  const ready = !!session.data?.authenticated && !choice.isPending;
  useFocusHeading(ready);
  if (!ready) return null;

  const current = session.data?.organizationId ?? null;
  // IDN-7: la lista ya viene filtrada por el servidor (la empresa inactiva no está); se descarta la actual por si acaso.
  const others = (choice.data?.options ?? []).some((option) => option.id !== current);

  /** Salir: lo mismo que el menú de la cuenta (POST /logout con el token) y vuelve al ingreso. */
  const signOut = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSending(true);
    try {
      const response = await fetch("/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
      });
      if (response.ok) window.location.assign("/login");
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthPage title={title}>
      <AuthNotice tone="warning">{L("ORG_INACTIVE_BODY")}</AuthNotice>
      <form method="post" action="/logout" onSubmit={signOut}>
        <AuthActions
          secondary={
            others ? (
              <a className="tm-btn secondary" href="/portal/organizaciones">
                {L("ORG_INACTIVE_SWITCH")}
              </a>
            ) : null
          }
        >
          <SubmitButton sending={sending}>{L("ACCOUNT_SIGN_OUT")}</SubmitButton>
        </AuthActions>
      </form>
    </AuthPage>
  );
}
